<template>
    <div
    v-for="menuEntry in parsedMenuItems"
    class="menu-item-wrapper"
    :key="menuEntry.id"
	:class="{'active': menuEntry.showSubItems, 'mobile': getMobileAppState, 'collapsed': menuCollapsed}">
        <!-- Menu Items -->
        <navigation-primary-menu-main
        :menuCollapsed="menuCollapsed"
        :menuEntry="menuEntry"
        :selected="selectedMainItem === menuEntry.link && !selectedSubItem"
        :subItemSelected="selectedSubItem && selectedSubItem.includes(menuEntry.link)"
        :showingSubItems="menuEntry.showSubItems"
        @clicked="mainItemClickHandler(menuEntry)"/>
        <!-- Menu Sub-items -->
        <transition name="appear-sliding">
            <div v-if="menuEntry.showSubItems && !menuCollapsed" class="menu-sub-items">
                <navigation-primary-menu-sub
                v-for="childEntry in menuEntry.children"
                :menuCollapsed="menuCollapsed"
                :key="childEntry.id"
                :selected="selectedSubItem === childEntry.link"
                :submenuEntry="childEntry"
                @clicked="subItemClickHandler(childEntry)"/>
            </div>
        </transition>
    </div>
</template>

<script setup>
import NavigationPrimaryMenuMain from '@/components/generic/navigation/primary/MenuMain'
import NavigationPrimaryMenuSub from '@/components/generic/navigation/primary/MenuSub'
/* Vue */
import { ref, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* Emmitters */
const emit = defineEmits(['clicked', 'expandMenu'])

/** props received
@menuCollapsed {Boolean} to trigger collapsed variant of menu components
@menuItems {Array} with menu list
**/
const props = defineProps({
    menuCollapsed: Boolean,
	menuItems: Array
})

const { updateSessionTimer } = mapActions(moduleMapper.SESSION)
const { getSessionTime, getSessionExpiredState } = mapGetters(moduleMapper.SESSION)
const { getMobileAppState } = mapGetters()

const parsedMenuItems = ref(props.menuItems)

const selectedMainItem = ref(null)
const selectedSubItem = ref(null)
const currentRoute = useRoute()

const mainItemClickHandler = (mainItem) => {
    // if mainItem isn't terminal
    if (!mainItem.terminal && mainItem.children?.length) {
        parsedMenuItems.value.map(el => {
            el.id === mainItem.id ? mainItem.showSubItems = !mainItem.showSubItems : el.showSubItems = el.id === mainItem.id
        })
        emit('expandMenu')
        return
    }
    emit('clicked', mainItem)
}

const subItemClickHandler = (subItem) => {
    emit('clicked', subItem)
}

const validateCurrentRoute = () => {
    if (getMobileAppState.value) return

    const parsedCurrentRoute = currentRoute.path
    const separatedRoute = parsedCurrentRoute.split('/')
    // this ensures the correct mainItem is selected when there are other links with the same words
    const parsedMainItem = ([separatedRoute[0], separatedRoute[1]]).join('/')
    // this ensures the correct subItem is selected when there is an inner (third-level) link
    const parsedSubItem = ([separatedRoute[0], separatedRoute[1], separatedRoute[2]]).join('/')
    /* Reseting this before applying a new value */
    selectedMainItem.value = null
    selectedSubItem.value = null

    props.menuItems.forEach(item => {
        if (parsedMainItem.includes(item.link)) {
            // if item matches the current route
            if (item.children && item.children.length) {
            // if item has children set selected values and open subItems
                selectedMainItem.value = item.link
                selectedSubItem.value = parsedSubItem
                item.showSubItems = true
            } else {
            // if item does not have children set selected values
                selectedSubItem.value = null
                selectedMainItem.value = item.link
            }
        } else {
            // if item does not match current route close its subItems
            item.showSubItems = false
        }
    })
}

watch(() => currentRoute.path, (newVal, oldVal) => {
    if (process.env.VUE_APP_MOCKS !== 'true') startSeconds.value = getSessionTime.value
    validateCurrentRoute()
})

watch(() => props.menuItems, (newVal, oldVal) => {
    parsedMenuItems.value = props.menuItems
})

watch(() => getSessionExpiredState.value, (newVal, oldVal) => {
    if (newVal) {
        clearInterval(interval)
        updateSessionTimer(0)
    } else {
        startTimer()
    }
})

const startSeconds = ref(0)
let interval = null

const startTimer = () => {
	startSeconds.value = getSessionTime.value

    if (interval) {
		clearInterval(interval)
	}

    interval = setInterval(() => {
        if (startSeconds.value <= 1 || getSessionExpiredState.value) {
            clearInterval(interval)
			updateSessionTimer(0)
        } else {
            startSeconds.value--
			updateSessionTimer(startSeconds.value)
        }
    }, 1000)
}

startTimer()

onMounted(() => {
    validateCurrentRoute()
})
</script>

<style lang="scss" scoped>
.menu-item-wrapper {
    width: 252px;
    min-width: 48px;
    margin-bottom: 2px;

    transition: all 0.4s ease-in;
    overflow: hidden;
    border-radius: 8px;

    &.mobile, &.collapsed {
        width: 100%;
    }

    .menu-sub-items {
        padding: 8px;
    }

    &.active {
        .menu-sub-items {
            background-color: rgba(var(--menu-main-bg-color-open), var(--menu-bg-opacity-state-open));
        }

        &.mobile {
            .menu-sub-items {
                background-color: var(--secondary-light-3);
            }
        }
    }
}

.appear-sliding-enter-active,
.appear-sliding-leave-active {
	max-height: 600px;
	opacity: 1;

	transition: all 0.4s ease-in-out;
}

.appear-sliding-enter-from,
.appear-sliding-leave-to {
	max-height: 0;
	opacity: 0.2;

	transition: all 0.4s ease-in-out;
}
</style>
